import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { ibeSalonBusinessInfoAndHoursRead, ibeSalonBusinessInfoAndHoursUpdate } from 'actions/salon/beSalonBusinessInfoAndHours.action';
import { feSalonUpdate } from 'actions/ui.actions';
import ICO_HOUR from 'assets/images/menu/settings-menu/building-purple.svg';
import validateEmail from 'bl/validation/validateEmail.bl';
import { validatePhone } from 'bl/validation/validatePhone.bl';
import Button from 'components/form/Button.component';
import EnumeratedInput from 'components/form/EnumeratedInput.component';
import TypedOption from 'components/form/TypedOption.component';
import Foldout from 'components/generic/Foldout.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import { STATES_MAP, UK_COUNTIES } from 'constants/address/counties.const';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import useAsyncFnFix from 'hooks/useAsyncFnFix.hook';
import { usePrepareReadWrite } from 'hooks/usePrepare.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import logger from 'utils/logger.util';

import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import SectionTitle from 'settings/online-booking/pages/SectionTitle.component';

import CN from './BusinessInformation.module.scss';

const L = logger('BusinessInformation');

const BusinessInformation = ({ feSalonUpdate }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { isSalonWithVAT, isSalonInUS } = useGilt();

  const {
    qj: salonData,
    up: saveSalonInfo,
    qr: resultSalonInfoRead,
    mr: resultHourUpdate,
    isLoading,
  } = usePrepareReadWrite({
    reader: ibeSalonBusinessInfoAndHoursRead,
    writer: ibeSalonBusinessInfoAndHoursUpdate,
  });

  const [formState, setFormState] = useState(null);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isNameValid, setIsNameValid] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  useEffect(() => {
    if (salonData) setFormState(salonData);
  }, [salonData]);

  const handleInputChange = useCallback((field, value) => {
    const fieldValue = typeof value === 'string' ? value : value.ev;

    setFormState((prev) => ({
      ...prev,
      [field]: fieldValue,
      isVatRegistered: field === 'vatNumber' && !fieldValue ? false : prev.isVatRegistered,
    }));

    if ('email' === field) setIsEmailValid(validateEmail({ value: fieldValue }));
    if ('name' === field) setIsNameValid(fieldValue.trim() !== '');
    if ('phone' === field) setIsPhoneValid(validatePhone({ value: fieldValue }));
  }, []);

  const handleVatChange = useCallback(() => {
    setFormState((prev) => {
      // select only if VAT num is filled
      if (!prev.vatNumber) return prev;
      return { ...prev, isVatRegistered: !prev.isVatRegistered };
    });
  }, []);

  const isFormValid = isEmailValid && isNameValid && isPhoneValid;

  const [resultInfoSave, handleSave] = useAsyncFnFix(async () => {
    if (!formState) return;
    // NOTE: update both /business-info-and-opening-hours/  and old core api
    const formData = {
      name: formState.name,
      email: formState.email,
      phone: formState.phone,
      city: formState.city,
      vat_number: formState.vatNumber,
      vatNumber: formState.vatNumber,
      state: formState.state,
      isVatRegistered: formState.isVatRegistered,
      is_vat_registered: formState.isVatRegistered,
      address_line_1: formState.addressLineFirst,
      addressLineFirst: formState.addressLineFirst,
      address_line_2: formState.addressLineNext,
      addressLineNext: formState.addressLineNext,
      postcode: formState.postcode,
    };

    const result = await saveSalonInfo({ data: formData });
    if (!result?.is?.ok) return;

    feSalonUpdate(formData);
  }, [feSalonUpdate, formState, saveSalonInfo]);

  const selectedCountry = salonData?.countryCode ? salonData?.countryCode : 'GB';
  const selectedLocationOptions = STATES_MAP?.[selectedCountry] || UK_COUNTIES;

  if (isLoading || !formState) return <OverlaySpinner />;

  L.debug('()', { resultInfoSave, resultSalonInfoRead, resultHourUpdate });

  const stateLabel = isSalonInUS ? t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.stateLabel') : t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.countyLabel');

  const statePlaceholder = isSalonInUS
    ? t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.statePlaceholder')
    : t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.countyPlaceholder');

  return (
    <div
      data-bem="BusinessInformation"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <Foldout isOpen tag="BusinessInformation" src={ICO_HOUR} width="md" title={t('staffAndBusiness.businessInfoAndHours.businessInfo.foldoutTitle')}>
        <SectionSeparator />
        <SectionTitle value={t('staffAndBusiness.businessInfoAndHours.businessInfo.section1Title')} />

        <SectionContent>
          <div data-bem="SalonSetup__row" className={CN.row}>
            <div className={CN.inputContainer}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.salonNameLabel')}</span>
              <input
                type="text"
                value={formState.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={cn({
                  [CN.input]: true,
                  [CN.invalid]: !isNameValid,
                })}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.salonNamePlaceholder')}
                tabIndex={1}
              />
              {!isNameValid && <span className={CN.error}>{t('common.errors.validation.noName')}</span>}
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
            <div className={CN.inputContainer}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.emailLabel')}</span>
              <input
                type="email"
                value={formState.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={cn({
                  [CN.input]: true,
                  [CN.invalid]: !isEmailValid,
                })}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.emailPlaceholder')}
                tabIndex={2}
              />
              {!isEmailValid && <span className={CN.error}>{t('common.errors.validation.notValidEmail')}</span>}
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
          </div>
          <div data-bem="SalonSetup__row" className={CN.row}>
            <div className={CN.inputContainer}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.phoneLabel')}</span>
              <PhoneInput
                defaultCountry={formState.country?.code}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.phonePlaceholder')}
                value={formState.phone || ''}
                onChange={(value) => handleInputChange('phone', value)}
                tabIndex={3}
              />
              {!isPhoneValid && <span className={CN.error}>{t('common.errors.validation.notValidPhoneNumber')}</span>}

              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
            {isSalonWithVAT && (
              <div className={CN.inputContainer}>
                <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.taxLabel')}</span>
                <div className={CN.vatContainer}>
                  <TypedOption type="simple-checkbox" checked={formState.isVatRegistered} onCheck={handleVatChange} disabled={!formState.vatNumber}>
                    <div className={CN.vatLabelName}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.taxCheckbox')}</div>
                  </TypedOption>
                  <input
                    type="text"
                    value={formState.vatNumber || ''}
                    onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                    className={cn({
                      [CN.input]: true,
                      [CN.vatInput]: true,
                    })}
                    placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.taxPlaceholder')}
                    tabIndex={4}
                  />
                  <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
                </div>
              </div>
            )}
          </div>
        </SectionContent>
        <SectionSeparator />
        <SectionTitle value={t('staffAndBusiness.businessInfoAndHours.businessInfo.section2Title')} />

        <SectionContent>
          <div className={CN.grid}>
            <div className={`${CN.inputContainer} ${CN.address1}`}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.address1Label')}</span>
              <input
                type="text"
                value={formState.addressLineFirst || ''}
                onChange={(e) => handleInputChange('addressLineFirst', e.target.value)}
                className={CN.input}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.address1Placeholder')}
                tabIndex={5}
              />
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
            <div className={`${CN.inputContainer} ${CN.state}`}>
              <span className={CN.inputLabel}>{stateLabel}</span>
              <EnumeratedInput value={formState.state || ''} options={selectedLocationOptions} placeholder={statePlaceholder} onChange={(e) => handleInputChange('state', e.string)} tabIndex={7} />
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>

            <div className={`${CN.inputContainer} ${CN.address2}`}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.address2Label')}</span>
              <input
                type="text"
                value={formState.addressLineNext || ''}
                onChange={(e) => handleInputChange('addressLineNext', e.target.value)}
                className={CN.input}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.address2Placeholder')}
                tabIndex={8}
              />
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
            <div className={`${CN.inputContainer} ${CN.postcode}`}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.postcodeLabel')}</span>
              <input
                type="text"
                value={formState.postcode || ''}
                onChange={(e) => handleInputChange('postcode', e.target.value)}
                className={CN.input}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.postcodePlaceholder')}
                tabIndex={9}
              />
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>

            <div className={`${CN.inputContainer} ${CN.city}`}>
              <span className={CN.inputLabel}>{t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.cityLabel')}</span>
              <input
                type="text"
                value={formState.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className={CN.input}
                placeholder={t('staffAndBusiness.businessInfoAndHours.businessInfo.inputFields.cityPlaceholder')}
                tabIndex={10}
              />
              <TooltipForReceptionist permission={PERM.canEditBusinessInfoHours} where={IN.salon} />
            </div>
          </div>
          <div className={CN.buttonContainer}>
            <Button variant="primary" onClick={handleSave} xSpace disabled={!isFormValid}>
              {t('common.buttons.saveChanges')}
            </Button>
          </div>
        </SectionContent>
      </Foldout>
    </div>
  );
};

const BusinessInformationHoc = connect(
  null,
  mapActionsToDispatchToProps({
    feSalonUpdate,
  }),
)(BusinessInformation);

export default BusinessInformationHoc;
